import { 
    Dropdown, 
    Option, 
    Button, 
    Label, 
    makeStyles, 
    shorthands, 
    tokens, 
    OverlayDrawer, 
    DrawerHeader, 
    DrawerHeaderTitle, 
    DrawerBody,
    Input,
    Tooltip
} from "@fluentui/react-components";
import { 
    FilterRegular, 
    DismissRegular, 
    ArrowCounterclockwiseRegular,
    SearchRegular
} from "@fluentui/react-icons";
import { getOptionsFromDimension, getKeyFriendlyName } from "../../pages/dashboard/DocumentSearchFilterOptions";
import { FilterKey } from "../../pages/dashboard/DocumentSearchFilters";
import { ChartTitleFriendlyNames } from "../../utils/FriendlyNameMappings";
import React, { useState } from 'react';
import { FilterPanelProps } from "./FilterPanelProps";
import { getDefaultSearchParams } from "../../controller/dashboardController";
import "./FilterPanel.css";

// Define local interface for Dropdown options to replace v8 IDropdownOption

interface IChartDataPoint {
    legend: string;
    data: number;
    color: string;
    key: string;
    onClick?: () => void;
    chartTitle?: string;
}

interface IChartProps {
    chartTitle: string;
    chartData: IChartDataPoint[];
}

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    searchContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalS),
        flexWrap: 'wrap',
    },
    searchBox: {
        minWidth: '300px',
        flexGrow: 1,
    },
    filterButtonContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '8px',
        marginBottom: '4px',
    },
    filterPillsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('0.25rem'),
        alignItems: 'center',
        marginLeft: '12px',
    },
    pill: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding('2px', '8px'),
        fontSize: tokens.fontSizeBase200,
    },
    pillButton: {
        marginLeft: '4px',
        minWidth: '20px',
        height: '20px',
        ...shorthands.padding('0'),
    },
    drawer: {
        width: '600px',
        maxWidth: '100vw',
    },
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    dateTimeContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    dateTimeRow: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'flex-end',
    },
    datePicker: {
        flexGrow: 2,
        minWidth: '200px',
        display: 'flex',
        flexDirection: 'column',
    },
    timeInput: {
        flexGrow: 1,
        minWidth: '120px',
        display: 'flex',
        flexDirection: 'column',
    },
    dropdown: {
        width: '100%',
        marginBottom: '10px',
        display: 'flex',
        flexDirection: 'column',
    },
    chartContainer: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalS),
        marginTop: tokens.spacingVerticalL,
        width: '100%',
    },
    chartRow: {
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        ...shorthands.gap(tokens.spacingHorizontalL),
        width: '100%',
    },
    chartItem: {
        minWidth: '300px',
        flexGrow: 1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding('4px'),
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorBrandStroke1,
            outlineOffset: '-2px',
        },
    },
    inputControl: {
        width: '100%',
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
        boxShadow: tokens.shadow4,
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    drawerBody: {
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.padding(tokens.spacingVerticalM),
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalL),
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('4px'),
        padding: '4px',
    },
    tooltipValue: {
        fontSize: '18px',
        fontWeight: 'bold',
    },
    tooltipLabel: {
        fontSize: '12px',
        fontWeight: 600,
    },
    tooltipSubtext: {
        fontSize: '10px',
        color: tokens.colorNeutralForeground2,
    }
});

const SimpleStackedBarChart = ({ data }: { data: IChartProps }) => {
    const styles = useStyles();
    const total = data.chartData.reduce((acc: number, curr: IChartDataPoint) => acc + curr.data, 0);
    if (total === 0) return null;

    return (
        <div style={{ width: '100%' }}>
            <div style={{ marginBottom: 4, fontWeight: 600 }}>{data.chartTitle}</div>
            <div style={{ display: 'flex', height: 20, width: '100%', borderRadius: 4, overflow: 'hidden' }}>
                {data.chartData.map((point: IChartDataPoint, index: number) => (
                    <Tooltip
                        key={index}
                        content={
                            <div className={styles.tooltipContainer}>
                                <span className={styles.tooltipLabel} style={{ color: point.color }}>{point.legend}</span>
                                <span className={styles.tooltipValue}>{point.data}</span>
                                <span className={styles.tooltipSubtext}>{data.chartTitle}</span>
                            </div>
                        }
                        relationship="label"
                    >
                        <div
                            role="img"
                            style={{
                                width: `${(point.data / total) * 100}%`,
                                backgroundColor: point.color,
                                height: '100%',
                                cursor: point.onClick ? 'pointer' : 'default'
                            }}
                            onClick={point.onClick}
                        />
                    </Tooltip>
                ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {data.chartData.map((point: IChartDataPoint, index: number) => (
                    <div 
                        key={index} 
                        style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            fontSize: 12,
                            cursor: point.onClick ? 'pointer' : 'default',
                            padding: '2px 4px',
                            borderRadius: '4px',
                            transition: 'background-color 0.2s'
                        }}
                        onClick={point.onClick}
                        onKeyDown={(e) => {
                            if (point.onClick && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                point.onClick();
                            }
                        }}
                        {...(point.onClick ? { tabIndex: 0, role: 'button', 'aria-label': `Filter by ${point.legend}` } : {})}
                        onMouseEnter={(e) => {
                            if (point.onClick) {
                                e.currentTarget.style.backgroundColor = tokens.colorNeutralBackground3;
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={point.onClick ? `Click to filter by ${point.legend}` : ''}
                    >
                        <div style={{ width: 8, height: 8, backgroundColor: point.color, marginRight: 4, borderRadius: '50%' }} />
                        <span>{point.legend}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

function FilterPanel(props: FilterPanelProps) {
    const styles = useStyles();
    const [selectedKeys, setSelectedKeys] = React.useState<(string[] | number[] | null)[]>([null, null, null, null, null, null]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    // Handling filter changes
    const onDropdownChange = (event: any, data: any, filterKey: FilterKey, index: number): void => {
        // v9 Dropdown onOptionSelect gives us the selected options
        // But here we are managing selection manually via props.setSearchParams
        // We need to handle multi-select logic.
        
        // Since v9 Dropdown is controlled differently, let's adapt.
        // data.selectedOptions is an array of keys.
        
        const newSelectedKeys = data.selectedOptions;
        
        // Update local state
        const updatedKeys = [...selectedKeys];
        updatedKeys[index] = newSelectedKeys.length > 0 ? newSelectedKeys : null;
        setSelectedKeys(updatedKeys);

        // Update search params
        props.setSearchParams({
            ...props.searchParams,
            filters: {
                ...props.searchParams.filters,
                [filterKey]: newSelectedKeys
            }
        });
    };

    const onSearchDropdownChange = (event: any, data: any): void => {
        // data.optionValue is the key
        const isExact = data.optionValue === 'Exact Search';
        props.setExactSearch(isExact);
    };

    // Convert donut chart data to stacked bar chart data
    function getStackedBarData(list: string, aggregatedTotals: any): IChartDataPoint[] {
        return Object.entries<number>(aggregatedTotals).map(([key, value], index: number) => {
            const friendlyKey = getKeyFriendlyName(list, key);
            return {
                legend: friendlyKey,
                data: value,
                color: getColor(list, friendlyKey),
                key: key
            };
        });
    }

    // Hardcoded colors since we removed getTheme
    const _colors: { [key: string]: { [key: string]: string } } =
    {
        'status':
        {
            "In Progress": '#0078d4', // blue
            "Failed": '#a80000', // red
            "Succeeded": '#107c10', // green
            "Warning": '#d83b01', // orange
            "Cancel Succeeded": '#0b6a0b', // greenDark
            "Cancel Failed": '#750b1c', // redDark
            "Cancel In Progress": '#004e8c', // blueDark
            "Batched": '#8764B8', // purple
            "Sent": '#0078d4' // blue
        },
        'mode': {
            "UAT": '#0078d4',
            "Live": '#107c10',
            "Test": '#ffb900' // yellow
        },
        'documentType': {
            'Invoice': '#b4009e', // magenta
            'Credit Note': '#d83b01', // orange
            'Payment Receipt': '#0078d4', // blue
            'Debit Note': '#ffb900' // yellow
        },
        'channel': {
            'Commercial': '#008272', // teal
            'Consumer': '#5c2d91', // purple
            'MilanInvoicing': '#a80000' // red
        }
    };

    // Assign color to chart elements
    function getColor(list: string, key: string): string {
        return _colors[list]?.[key] ?? '#0078d4';
    }

    const handleDataClick = (data: IChartDataPoint) => {
        props.setSearchParams({
            ...props.searchParams,
            filters: {
                ...props.searchParams.filters,
                [data.chartTitle ?? '']: [data.key]
            }
        });
    };

    // Map raw data to chart props format
    function mapToStackedBarChartData(raw: any[]): IChartProps[] {
        return raw.map(row => {
            // Convert data to chart format
            const chartPoints = getStackedBarData(row.dimension, row.aggregatedTotals);

            // Add chart title and click handlers
            const chartData = chartPoints.map(point => ({
                ...point,
                chartTitle: row.dimension,
                onClick: () => handleDataClick({
                    ...point,
                    chartTitle: row.dimension,
                    key: point.key as string
                } as IChartDataPoint)
            }));

            return {
                chartTitle: ChartTitleFriendlyNames[row.dimension] || row.dimension,
                chartData: chartData
            };
        });
    }

    function resetSearchData() {
        props.setSearchParams(getDefaultSearchParams());
        setSelectedKeys([null, null, null, null, null]);
    }

    function setEndTimeToNow() {
        props.setSearchParams({ ...props.searchParams, endTime: new Date() });
    }

    // Date and time conversion functions
    function convertUTCDateToLocalDate(date: Date) {
        var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        var offset = date.getTimezoneOffset() / 60;
        var hours = date.getHours();
        newDate.setHours(hours - offset);
        return newDate;
    }

    function endTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = props.searchParams.endTime
        if (props.useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            utcDate.setSeconds(0, 0)
            newDate = convertUTCDateToLocalDate(utcDate);
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
            newDate.setSeconds(0, 0)
        }
        props.setSearchParams({ ...props.searchParams, endTime: newDate });
    }

    function endDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (props.useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(props.searchParams.endTime.getHours())
            newDate.setMinutes(props.searchParams.endTime.getMinutes())
            props.setSearchParams({ ...props.searchParams, endTime: newDate });
        }
    }

    function startDateChanged(date: Date | null | undefined) {
        var newDate = date
        if (newDate && newDate !== null) {
            if (props.useUTC) {
                newDate = convertUTCDateToLocalDate(newDate);
            }
            newDate.setHours(props.searchParams.startTime.getHours())
            newDate.setMinutes(props.searchParams.startTime.getMinutes())
            props.setSearchParams({ ...props.searchParams, startTime: newDate });
        }
    }

    function startTimeChanged(e: React.ChangeEvent<HTMLInputElement>) {
        const timeString = e.target.value;
        if (!timeString) return;
        
        const [hours, minutes] = timeString.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes);

        var newDate = props.searchParams.startTime
        if (props.useUTC) {
            var utcDate = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
            utcDate.setHours(date.getHours())
            utcDate.setMinutes(date.getMinutes())
            newDate = convertUTCDateToLocalDate(utcDate);
            utcDate.setSeconds(0, 0)
        }
        else {
            newDate.setHours(date.getHours())
            newDate.setMinutes(date.getMinutes())
            newDate.setSeconds(0, 0)
        }
        props.setSearchParams({ ...props.searchParams, startTime: newDate });
    }

    // Chart dimensions
    const barChartDimensions: FilterKey[] = [
        'status',
        'documentType',
        'channel',
        'mode'
    ];

    // Filter dropdown dimensions
    const filterDropDowns: FilterKey[] = [
        'eInvPartnerId',
        'countryCode',
        'supplier',
        'errorCode',
        'salesMotion',
        'paymentType'
    ];

    function GetDateValue(useUTC: boolean, date: Date): Date {
        return useUTC ? new Date(date.getTime() + date.getTimezoneOffset() * 60000) : date;
    }

    function formatTimeForInput(date: Date): string {
        const d = GetDateValue(props.useUTC, date);
        return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }

    function formatDateForInput(date: Date): string {
        const d = GetDateValue(props.useUTC, date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    const onInputDateChange = (ev: any, data: any, isStart: boolean) => {
        const dateString = data.value;
        if (!dateString) return;
        const [year, month, day] = dateString.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        if (isStart) startDateChanged(dateObj);
        else endDateChanged(dateObj);
    }

    // Handle removing a filter pill
    const removeFilter = (dimension: FilterKey, value: string) => {
        props.setSearchParams({
            ...props.searchParams,
            filters: {
                ...props.searchParams.filters,
                [dimension]: props.searchParams.filters[dimension].filter((val) => val !== value)
            }
        });

        // Update the selected keys state as well
        const index = filterDropDowns.findIndex(item => item === dimension);
        if (index >= 0 && selectedKeys[index] != null) {
            const array = selectedKeys[index] as string[];
            const indexToRemove = array.indexOf(value);
            if (indexToRemove >= 0) {
                array.splice(indexToRemove, 1);
                setSelectedKeys(Object.assign([...selectedKeys], {
                    [index]: array.length > 0 ? [...array] : null
                }));
            }
        }
    };    // Generate filter pills
    const renderFilterPills = () => {
        const pills = [];

        // Time range pill - respect UTC setting
        const displayStartDate = GetDateValue(props.useUTC, props.searchParams.startTime);
        const displayEndDate = GetDateValue(props.useUTC, props.searchParams.endTime);

        const startDate = displayStartDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
        const startTime = displayStartDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const endDate = displayEndDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
        const endTime = displayEndDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        const timeZoneLabel = props.useUTC ? ' UTC' : '';

        // Handler to reset only the time range (startTime and endTime)
        const resetTimeRange = () => {
            props.setSearchParams({
                ...props.searchParams,
                startTime: getDefaultSearchParams().startTime,
                endTime: getDefaultSearchParams().endTime
            });
        };

        pills.push(
            <div key="timerange" className={styles.pill}>
                <span title={`${startDate} ${startTime} - ${endDate} ${endTime}${timeZoneLabel}`}>
                    {`${startDate} ${startTime} - ${endDate} ${endTime}${timeZoneLabel}`}
                </span>
                <Button
                    icon={<ArrowCounterclockwiseRegular fontSize={12} />}
                    title="Reset time range"
                    aria-label="Reset time range"
                    onClick={resetTimeRange}
                    className={styles.pillButton}
                    appearance="transparent"
                    size="small"
                />
            </div>
        );

        // Filter dimension pills
        Object.entries(props.searchParams.filters).forEach(([dimension, values]) => {
            if (values && values.length > 0) {
                values.forEach((value: string) => {
                    // Get friendly names for both dimension and value
                    const friendlyDimension = ChartTitleFriendlyNames[dimension] || dimension;
                    const friendlyValue = getKeyFriendlyName(dimension, value);

                    pills.push(
                        <div key={`${dimension}-${value}`} className={styles.pill}>
                            <span title={`${friendlyDimension}: ${friendlyValue}`}>
                                <b>{friendlyDimension}:</b> {friendlyValue}
                            </span>
                            <Button
                                icon={<DismissRegular fontSize={12} />}
                                title={`Remove ${friendlyValue} filter`}
                                aria-label={`Remove ${friendlyValue} filter`}
                                onClick={() => removeFilter(dimension as FilterKey, value)}
                                className={styles.pillButton}
                                appearance="transparent"
                                size="small"
                            />
                        </div>
                    );
                });
            }
        });

        return pills.length > 0 ? (
            <div className={styles.filterPillsContainer}>
                {pills}
            </div>
        ) : null;
    };

    return (
        <div className={styles.container}>
            <div className={styles.searchContainer}>
                <Input
                    className={styles.searchBox}
                    placeholder="Search for document ids, partners, customer ids, etc.."
                    contentBefore={<SearchRegular />}
                    onChange={(ev, data) => props.setSearchParams({ ...props.searchParams, searchText: data.value })}
                />
                <Dropdown
                    onOptionSelect={onSearchDropdownChange}
                    defaultValue="Exact Search"
                    style={{ minWidth: '150px' }}
                    aria-label="Search type"
                >
                    <Option value="Exact Search">Exact Search</Option>
                    <Option value="WildCard Search">WildCard Search</Option>
                </Dropdown>
                <Button
                    onClick={resetSearchData}
                    icon={<ArrowCounterclockwiseRegular />}
                >
                    Reset Search
                </Button>
            </div>

            {/* Filters Button below search bar, in front of filter pills */}
            <div className={styles.filterButtonContainer}>
                <Button
                    onClick={() => setIsPanelOpen(true)}
                    icon={<FilterRegular />}
                >
                    Filters
                </Button>
                {/* Filter Pills */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {renderFilterPills()}
                </div>
            </div>

            {/* Bar Charts */}
            <div className={styles.chartContainer}>
                <div className={styles.chartRow}>
                    {mapToStackedBarChartData(barChartDimensions
                        .map(dimension => props.facetResults.find(f => f.dimension === dimension))
                        .filter(Boolean)
                    ).map((item, index) => {
                        const dimension = barChartDimensions[index];
                        return (
                            <div
                                key={`chart-${dimension}`}
                                id={`chart-${dimension}`}
                                className={styles.chartItem}
                                role="group"
                                aria-label={`Filter by ${ChartTitleFriendlyNames[dimension] || dimension}`}
                            >
                                <SimpleStackedBarChart data={item} />
                            </div>
                        );
                    })}
                </div>
            </div>

            <OverlayDrawer
                open={isPanelOpen}
                onOpenChange={(_, { open }) => setIsPanelOpen(open)}
                position="end"
                className={styles.drawer}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<DismissRegular />}
                                onClick={() => setIsPanelOpen(false)}
                            />
                        }
                    >
                        Filter Options
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody className={styles.drawerBody}>
                    <div className={styles.card}>
                        <div className={styles.dateTimeContainer}>
                            <Label weight="semibold">Time Range</Label>
                            {/* First row: Start Date and Time */}
                            <div className={styles.dateTimeRow}>
                                <div className={styles.datePicker}>
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        className={styles.inputControl}
                                        value={formatDateForInput(props.searchParams.startTime)}
                                        onChange={(ev, data) => onInputDateChange(ev, data, true)}
                                    />
                                </div>
                                <div className={styles.timeInput}>
                                    <Label>Start Time</Label>
                                    <Input
                                        type="time"
                                        className={styles.inputControl}
                                        value={formatTimeForInput(props.searchParams.startTime)}
                                        onChange={startTimeChanged}
                                    />
                                </div>
                            </div>

                            {/* Second row: End Date and Time with Now button */}
                            <div className={styles.dateTimeRow}>
                                <div className={styles.datePicker}>
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        className={styles.inputControl}
                                        value={formatDateForInput(props.searchParams.endTime)}
                                        onChange={(ev, data) => onInputDateChange(ev, data, false)}
                                    />
                                </div>
                                <div className={styles.timeInput}>
                                    <Label>End Time</Label>
                                    <Input
                                        type="time"
                                        className={styles.inputControl}
                                        value={formatTimeForInput(props.searchParams.endTime)}
                                        onChange={endTimeChanged}
                                    />
                                </div>
                                <Button
                                    onClick={setEndTimeToNow}
                                >
                                    Now
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.card}>
                        {filterDropDowns.map((dimension, index) => {
                            const facet = props.facetResults.find(f => f.dimension === dimension);
                            if (!facet) return null;

                            const options = getOptionsFromDimension(facet, true);

                            return (
                                <div key={dimension} className={styles.dropdown}>
                                    <Label id={`filter-label-${dimension}`}>{ChartTitleFriendlyNames[dimension] || dimension}</Label>
                                    <Dropdown
                                        multiselect
                                        className={styles.inputControl}
                                        placeholder="Select options..."
                                        selectedOptions={selectedKeys[index] as string[] || []}
                                        onOptionSelect={(ev, data) => onDropdownChange(ev, data, dimension, index)}
                                        aria-labelledby={`filter-label-${dimension}`}
                                    >
                                        {options.map((opt: any) => (
                                            <Option key={opt.key} value={opt.key} text={opt.text}>
                                                {opt.text}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                            );
                        })}
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
}

export default FilterPanel;
